/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { Case, Switch } from "react-if";
import { useEffectOnce, useUpdateEffect } from "react-use";
import { Box } from "@nordcloud/gnui";
import {
  BreadcrumbsBox,
  ChartsQueryState,
  ChartType,
  CostAnalysisChartHeader,
  DateRange,
  useCostAnalysis,
  usePeriod,
  useViewOthers,
} from "~/components";
import { useQueryState } from "~/hooks";
import { useCustomers } from "~/services/customers";
import { getFirstItem, isNotNil } from "~/tools";
import {
  ApplicationsAccumulatedCostView,
  ApplicationsCostPerApplicationView,
  ApplicationsCostPerCategoryView,
  ApplicationsCostPerServicesView,
} from "./views";
import { getApplicationsChartTypeOptions } from "./views/types";

const BREADCRUMBS = [
  {
    value: "cost-analysis",
    label: "Cost Analysis",
  },
];

const BREADCRUMBS_TAGS = [
  {
    color: "notification",
    text: "Beta",
  },
];

export function ApplicationsCostAnalysisPage() {
  const { userRole } = useCustomers();
  const applicationsChartTypeOptions =
    getApplicationsChartTypeOptions(userRole);
  const {
    state: { rangeStartDate, rangeEndDate, page, period: urlPeriod },
    updateQueryState,
  } = useQueryState<ChartsQueryState>();
  const [range, setRange] = useState<DateRange | undefined>(undefined);

  useEffect(() => {
    if (isNotNil(rangeStartDate) && isNotNil(rangeEndDate)) {
      setRange({
        from: rangeStartDate,
        to: rangeEndDate,
      });
    }
  }, [rangeStartDate, rangeEndDate]);

  const {
    period,
    granularity,
    setGranularity,
    setPeriod,
    isDailyChartViewDisabled,
    startDate,
    endDate,
  } = usePeriod(range);

  const { chartType, setChartType, isChartVisible } = useCostAnalysis({
    period,
    chartOptions: applicationsChartTypeOptions,
  });

  const {
    isOtherCostsSwitchVisible,
    isOtherCostsOpen,
    toggleOtherCosts,
    isOtherCostsSwitchEnabled,
    handleOtherCostsSwitch,
  } = useViewOthers({
    chartType: chartType.value,
  });

  useEffectOnce(() => {
    updateQueryState({
      limit: 10,
      page: page ?? 0,
    });
  });

  useUpdateEffect(() => {
    updateQueryState({ page: 0 });
  }, [urlPeriod]);

  return (
    <div>
      <BreadcrumbsBox
        title="Cost Analysis"
        labels={BREADCRUMBS}
        tags={BREADCRUMBS_TAGS}
      />
      <Box spacing="spacing04">
        <CostAnalysisChartHeader
          period={period}
          options={applicationsChartTypeOptions}
          chartType={chartType}
          range={range}
          granularity={granularity}
          disableDailyView={isDailyChartViewDisabled}
          isOtherCostsSwitchVisible={isOtherCostsSwitchVisible}
          showOtherCosts={isOtherCostsOpen}
          toggleOtherCosts={toggleOtherCosts}
          isOtherCostsSwitchDisabled={!isOtherCostsSwitchEnabled}
          onHandleClick={setPeriod}
          onSetRange={setRange}
          onSetGranularity={setGranularity}
          onChange={(item) => {
            setChartType(item ?? getFirstItem(applicationsChartTypeOptions));
          }}
        />
        <Switch>
          <Case condition={isChartVisible(ChartType.COST_PER_APPLICATION)}>
            <ApplicationsCostPerApplicationView
              showCostTable
              granularity={granularity}
              startDate={startDate}
              endDate={endDate}
              period={period}
              showOtherApplicationsCost={isOtherCostsOpen}
              handleOtherCostsSwitch={handleOtherCostsSwitch}
            />
          </Case>
          <Case condition={isChartVisible(ChartType.ACCUMULATED_COST)}>
            <ApplicationsAccumulatedCostView
              showCostTable
              startDate={startDate}
              endDate={endDate}
              granularity={granularity}
              period={period}
            />
          </Case>
          <Case condition={isChartVisible(ChartType.COST_PER_CATEGORY)}>
            <ApplicationsCostPerCategoryView
              showCostTable
              startDate={startDate}
              endDate={endDate}
              granularity={granularity}
              period={period}
              showOtherCategoriesCost={isOtherCostsOpen}
              handleOtherCostsSwitch={handleOtherCostsSwitch}
            />
          </Case>
          <Case condition={isChartVisible(ChartType.COST_PER_SERVICES)}>
            <ApplicationsCostPerServicesView
              showCostTable
              startDate={startDate}
              endDate={endDate}
              granularity={granularity}
              period={period}
              showOtherServicesCost={isOtherCostsOpen}
              handleOtherCostsSwitch={handleOtherCostsSwitch}
            />
          </Case>
        </Switch>
      </Box>
    </div>
  );
}
